/*
 * CHI-20 presentational pieces for /events/:slug/tickets, per Master UI/UX spec
 * /workspace/chi/ux/chi-20-ticket-page.md (copy verbatim). No data logic here
 * (see hooks/useTicketPurchase.ts). Contrast fixes are scoped to .chi-ticket-page
 * via --tp-* tokens (spec §6, D6); fields.tsx and global classes are untouched.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { inputStyle } from "@/components/memberForm/fields";
import { LockKeyhole, ReceiptText, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import type { Person, TicketPromotionQuote } from "@/lib/ticketingApi";
import type { AttendeeRowErrors } from "@/lib/ticketAttendees";
import type { OtpPhase } from "@/hooks/useTicketPurchase";

export function formatCents(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}

/** Page-scoped tokens and classes (spec §1, §6). */
export const TICKET_PAGE_CSS = `
.chi-ticket-page{--tp-label:#a6a9b7;--tp-link:#e7a6d0;--tp-focus:#f0a6d3;--tp-cta-end:#d2148a;--tp-warn:#d9c38d;--tp-ink:#f2f0f4;--tp-muted:#a7a9b8;--tp-panel:#141a2a;--tp-field:#202638;color:#d6d5de;background:#090d19;font-family:system-ui,sans-serif}
.chi-ticket-page h2{font:500 1rem/1.35 system-ui,sans-serif!important;letter-spacing:0}
.chi-ticket-page :focus-visible{outline:2px solid var(--tp-focus);outline-offset:2px}
.chi-ticket-page .tp-label{display:block;font-family:system-ui,sans-serif;font-size:.78rem;font-weight:600;letter-spacing:.015em;text-transform:none;color:var(--tp-label);margin-bottom:.45rem}
.chi-ticket-page a,.chi-ticket-page .tp-link{color:var(--tp-link)}
.chi-ticket-page .tp-link{display:inline-flex;align-items:center;min-height:44px}
.chi-ticket-page .tp-error{color:#f2a6cd;font-size:.82rem;margin-top:.3rem}
.chi-ticket-page .chi-btn-primary{background:linear-gradient(105deg,#7658ed 0%,#a53ad4 55%,var(--tp-cta-end) 100%);color:#fff;border:0;border-radius:.7rem;padding:.8rem 1.25rem;min-height:50px;font-weight:700}
.chi-ticket-page .chi-btn-outline{background:#202738;color:#f2f0f4;border:1px solid rgba(255,255,255,.1);border-radius:.65rem;padding:.55rem 1rem;min-height:44px}
.chi-ticket-card{background:var(--tp-panel);border:1px solid #343143;border-radius:1rem;padding:18px;margin-top:12px}
.chi-ticket-page input{background:var(--tp-field)!important;border:1px solid transparent!important;border-radius:.6rem!important;color:#f2f0f4!important;font-family:system-ui,sans-serif!important;min-height:44px}
.chi-ticket-page input::placeholder{color:#9094a5}
.chi-stepper button{width:44px;height:44px;border-radius:.65rem;border:1px solid rgba(255,255,255,.08);background:#202738;color:#fff;font-size:1.1rem}
.chi-stepper button:disabled{opacity:.4}
.chi-ticket-page .chi-stepper output{font-family:system-ui,sans-serif!important}
.chi-ticket-page .tp-layout{display:grid;grid-template-columns:minmax(0,1fr);gap:12px}
.chi-ticket-page .tp-checkout-header{height:50px;border-radius:14px;background:#34172f;display:flex;align-items:center;justify-content:space-between;padding:0 14px;margin-bottom:22px;color:#f2edf2;font-size:.72rem;letter-spacing:.05em}
.chi-ticket-page .tp-brand-return{display:inline-flex;align-items:center;min-height:44px;color:#f2edf2!important;text-decoration:none;font-weight:650}
.chi-ticket-page .tp-secure-badge{background:#49334a;border-radius:999px;padding:5px 10px;letter-spacing:0;font-size:.72rem}
.chi-ticket-page button{font:inherit}
.chi-ticket-page .tp-event-heading{margin:0 0 4px;font:700 clamp(1.35rem,2.5vw,1.65rem)/1.2 system-ui,sans-serif;color:var(--tp-ink)}
.chi-ticket-page .tp-event-meta{margin:0;color:var(--tp-muted);font-size:.9rem}
.chi-ticket-page .tp-event-kicker{color:#d8c59c;text-transform:uppercase;letter-spacing:.05em;font-size:.72rem;margin:0 0 7px}
.chi-ticket-page .tp-total-row{display:flex;justify-content:space-between;align-items:center;gap:1rem;border-top:1px solid #343747;padding-top:14px;margin-top:18px;font-size:1rem}
.chi-ticket-page .tp-total-row strong{font:700 1.45rem system-ui,sans-serif;color:var(--tp-ink)}
.chi-ticket-page .tp-hold-note{background:#2a2337;color:#dfcca2;padding:10px;border-radius:.6rem;margin:.8rem 0;font-size:.8rem}
.chi-ticket-page .tp-checkout-summary{padding:18px}
.chi-ticket-page .tp-ticket-header{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;column-gap:12px;row-gap:6px}
.chi-ticket-page .tp-ticket-title{grid-column:1/-1;display:flex;align-items:center;gap:.55rem;color:var(--tp-ink);font-weight:650}
.chi-ticket-page .tp-ticket-description{grid-column:1/-1}
.chi-ticket-page .tp-ticket-title svg{color:#d8c59c}
.chi-ticket-page .tp-ticket-price{margin:.7rem 0 0;color:var(--tp-muted);font-size:.78rem}
.chi-ticket-page .tp-ticket-price strong{display:block;color:var(--tp-ink);font-size:1.35rem;line-height:1.25}
.chi-ticket-page [data-testid="unit-price"]{font-size:inherit!important}
.chi-ticket-page .tp-ticket-price s{margin-left:.35rem;font-size:.72rem}
.chi-ticket-page .tp-ticket-badge{display:inline-flex;background:rgba(46,105,71,.25);color:#80d59a;border-radius:999px;padding:3px 8px;font-size:.7rem;margin-left:auto}
.chi-ticket-page .tp-ticket-header .chi-ticket-card{margin:0;border:0;background:transparent;padding:0}
.chi-ticket-page .tp-join-invitation{margin-top:14px;padding:12px 14px;border:1px solid #544a45;border-radius:.75rem;background:#211b25}
.chi-ticket-page .tp-join-invitation label{display:flex;align-items:center;gap:10px;min-height:44px;color:#f2f0f4;font-size:.86rem;font-weight:650;line-height:1.4;cursor:pointer}
.chi-ticket-page .tp-join-invitation input[type="checkbox"]{width:20px!important;height:20px!important;min-height:20px!important;flex:0 0 20px;padding:0!important;accent-color:#c48643;cursor:pointer}
.chi-ticket-page .tp-join-invitation p{margin:2px 0 0 30px;color:#c3b8b4;font-size:.76rem;line-height:1.5}
.chi-ticket-page .tp-order-heading{display:flex;align-items:center;gap:.55rem;border-top:1px solid #343747;padding-top:16px;margin:18px 0 12px;color:var(--tp-ink);font-size:1rem}
.chi-ticket-page .tp-order-heading svg{color:#d8c59c}
.chi-ticket-page .tp-order-summary-lines p{display:flex;justify-content:space-between;align-items:baseline;gap:1rem;margin:.6rem 0}
.chi-ticket-page .tp-guest-row{background:#1d2333;border:0;border-radius:.7rem;padding:12px;margin-top:8px}
.chi-ticket-page .tp-guest-row-header{display:flex;justify-content:flex-end;align-items:center;gap:.5rem;margin-bottom:8px}
.chi-ticket-page .tp-guest-ticket-label{font-size:.7rem;color:var(--tp-muted);margin-right:auto}
.chi-ticket-page .tp-guest-row .tp-label{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.chi-ticket-page .tp-guest-fields{grid-template-columns:minmax(0,1fr)!important;gap:.35rem!important}
.chi-ticket-page .tp-guest-fields>div{min-width:0}
.chi-ticket-page .tp-guest-row input{padding:.65rem .75rem!important}
.chi-ticket-page [data-slot="input-otp-slot"]{width:44px!important;height:44px!important;flex:none}
.chi-ticket-page .tp-guest-list-title{display:flex;align-items:center;justify-content:space-between}
.chi-ticket-page .tp-guest-list-title h2{margin:0}
.chi-ticket-page .tp-guest-count{font-size:.75rem;background:#292e3e;border-radius:999px;padding:4px 9px}
.chi-ticket-page .tp-aside{align-self:start}
.chi-ticket-page .tp-loading-summary{display:grid;gap:12px}
.chi-ticket-page .tp-skeleton-line{height:14px;border-radius:5px;background:linear-gradient(90deg,#242b3a,#303747,#242b3a);background-size:200% 100%;animation:tp-shimmer 1.4s ease-in-out infinite}
.chi-ticket-page .tp-skeleton-line:nth-child(2){width:72%}
.chi-ticket-page .tp-skeleton-line:nth-child(3){width:48%}
.tp-hold-dialog{z-index:100;width:calc(100% - 24px);max-width:440px;max-height:calc(100dvh - 24px);overflow-y:auto;border:1px solid #4a4053;border-radius:20px;background:#141a2a;color:#f2f0f4;padding:24px;box-shadow:0 24px 80px rgba(0,0,0,.55)}
.tp-hold-dialog__eyebrow{margin:0;color:#d8c59c;font-size:.72rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase}
.tp-hold-dialog__title{display:block;margin:10px 0 0;color:#f2f0f4;font:700 clamp(1.35rem,5vw,1.7rem)/1.2 system-ui,sans-serif}
.tp-hold-dialog__description{display:block;margin:10px 0 0;color:#c1c0cb;font-size:.94rem;line-height:1.55}
.tp-hold-dialog__status{margin:14px 0 0;border-radius:10px;background:#211f2b;padding:10px 12px;color:#e1d4b8;font-size:.85rem;line-height:1.45}
.tp-hold-dialog__actions{display:grid;gap:10px;margin-top:20px}
.tp-hold-dialog__actions button{min-height:48px;width:100%;border-radius:11px;padding:10px 14px;font:650 .92rem/1.3 system-ui,sans-serif}
.tp-hold-dialog__renew{border:0;background:#28784a;color:#fff}
.tp-hold-dialog__renew:hover:not(:disabled){background:#328a58}
.tp-hold-dialog__cancel{border:1px solid #65515b;background:#25212d;color:#f2d5d5}
.tp-hold-dialog__retry{border:1px solid #776b57;background:#25212d;color:#e8d8ae}
.tp-hold-dialog__actions button:disabled{cursor:wait;opacity:.58}
.tp-hold-inline-status{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:6px;color:#e1d4b8}
.tp-hold-inline-status button{min-height:44px;border:1px solid #776b57;border-radius:8px;background:#25212d;color:#f2f0f4;padding:7px 10px}
@keyframes tp-shimmer{to{background-position:-200% 0}}
.chi-ticket-page form{margin:0}
.tp-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
@media(min-width:740px){.chi-ticket-card{padding:18px}.chi-ticket-page .tp-layout{grid-template-columns:minmax(0,1fr) minmax(330px,.98fr);gap:14px}.chi-ticket-page .tp-aside{position:sticky;top:20px}.chi-ticket-page .tp-left{min-width:0}}
@media(max-width:720px){.chi-ticket-page .tp-checkout-summary{padding:16px}}
@media(max-width:480px){.chi-ticket-page main{padding-left:12px!important;padding-right:12px!important}.chi-ticket-page .tp-checkout-header{margin-bottom:18px}.chi-ticket-card{padding:12px}.chi-ticket-page .tp-layout{gap:8px}.chi-ticket-page .tp-guest-fields{grid-template-columns:minmax(0,1fr)!important}}
@media(max-width:480px){.tp-hold-dialog{padding:20px 16px;border-radius:16px}.tp-hold-dialog__actions{gap:8px}}
@media (prefers-reduced-motion: reduce){.chi-ticket-card,.chi-stepper,.chi-sticky-total{transition-duration:.01ms!important;animation:none!important}.chi-ticket-page .tp-skeleton-line{animation:none!important}}
@media (prefers-reduced-motion: reduce){.tp-hold-dialog{transition:none!important;animation:none!important}}
`;

const holdClockAnchors = new Map<number, { expiresAt: string; serverNow: string; deadlineClient: number }>();

function holdDeadlineOnClient(orderId: number, expiresAt: string, serverNow: string, receivedAt: number) {
  const cached = holdClockAnchors.get(orderId);
  if (cached?.expiresAt === expiresAt && cached.serverNow === serverNow) return cached.deadlineClient;
  const duration = Date.parse(expiresAt) - Date.parse(serverNow);
  const deadlineClient = Number.isFinite(duration) ? receivedAt + Math.max(0, duration) : Date.now();
  holdClockAnchors.set(orderId, { expiresAt, serverNow, deadlineClient });
  if (holdClockAnchors.size > 32) {
    const oldest = holdClockAnchors.keys().next().value;
    if (typeof oldest === "number") holdClockAnchors.delete(oldest);
  }
  return deadlineClient;
}

export function PageAlert({ kind, children }: { kind: "error" | "warning" | "offline" | "info"; children: ReactNode }) {
  const role = kind === "info" ? "status" : "alert";
  return <div role={role} data-kind={kind} style={{ padding: "0.75rem 1rem", borderRadius: "0.5rem", background: "rgba(255,255,255,0.06)", color: kind === "warning" ? "var(--tp-warn)" : undefined }}>{children}</div>;
}

export function DraftRestoredBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div role="status" style={{ background: "rgba(233,30,140,0.08)", borderLeft: "3px solid #E91E8C", padding: "0.75rem 1rem", display: "flex", justifyContent: "space-between" }}>
      <span>Your details are saved. Pick up where you left off.</span>
      <button type="button" aria-label="Dismiss" onClick={onDismiss} style={{ background: "none", border: 0, color: "inherit", minWidth: 44, minHeight: 44 }}>×</button>
    </div>
  );
}

export function EventSummaryHeader({ title, meta, promoChip, soldOut }: { title: string; meta?: string; promoChip?: string | null; soldOut?: boolean }) {
  const [eventDate, ...eventPlace] = (meta ?? "").split(" · ");
  return (
    <header className="tp-event-header">
      <p className="tp-event-kicker">Event registration{eventDate ? ` / ${eventDate}` : ""}</p>
      <h1 className="tp-event-heading">{title}</h1>
      {eventPlace.length > 0 && <p className="tp-event-meta">{eventPlace.join(" · ")}</p>}
      {soldOut ? <span style={{ background: "rgba(255,255,255,0.1)", borderRadius: 999, padding: "0.2rem 0.6rem", fontSize: "0.75rem" }}>Sold out</span>
        : promoChip ? <span style={{ background: "rgba(233,30,140,0.12)", color: "#F9A8D4", borderRadius: 999, padding: "0.2rem 0.6rem", fontSize: "0.75rem" }}>{promoChip}</span> : null}
    </header>
  );
}

export function PersonFields({ idPrefix, label, value, errors, onChange, onBlur, autoCompleteBuyer, readOnly, lockEmail }: {
  idPrefix: string; label?: string; value: Person; errors?: AttendeeRowErrors; onChange: (field: keyof Person, v: string) => void; onBlur?: () => void; autoCompleteBuyer?: boolean; readOnly?: boolean;
  /** CompleteTicketPurchase (temporary): the member's own row is locked to their account email. */
  lockEmail?: boolean;
}) {
  const nameId = `${idPrefix}-name`;
  const emailId = `${idPrefix}-email`;
  return (
    <div className={label ? "tp-guest-fields" : undefined} style={{ display: "grid", gap: "1.25rem" }}>
      <div>
        <label className="tp-label" htmlFor={nameId}>{label ? `${label} full name` : "Full name"}</label>
        <input id={nameId} value={value.name} readOnly={readOnly} onChange={e => onChange("name", e.target.value)} onBlur={onBlur} autoComplete={autoCompleteBuyer ? "name" : "off"} required aria-invalid={errors?.name ? true : undefined} aria-describedby={errors?.name ? `${nameId}-error` : undefined} style={inputStyle} />
        {errors?.name && <div id={`${nameId}-error`} className="tp-error">{errors.name}</div>}
      </div>
      <div>
        <label className="tp-label" htmlFor={emailId}>{label ? `${label} email` : "Email"}</label>
        <input id={emailId} type="email" inputMode="email" value={value.email} readOnly={readOnly || lockEmail} aria-readonly={lockEmail || undefined} data-locked={lockEmail ? "account-email" : undefined} onChange={e => onChange("email", e.target.value)} onBlur={onBlur} autoComplete={autoCompleteBuyer ? "email" : "off"} required aria-invalid={errors?.email ? true : undefined} aria-describedby={errors?.email ? `${emailId}-error` : undefined} style={inputStyle} />
        {errors?.email && <div id={`${emailId}-error`} className="tp-error">{errors.email}</div>}
      </div>
    </div>
  );
}

/** Buyer 6-digit code on the existing email-OTP login (hooks/useBuyerOtp). */
export function OtpInline({ email, phase, prefill, onSend, onVerify, onChangeEmail, resendSeconds = 30, guestMode = false, disabled = false }: {
  email: string; phase: OtpPhase; prefill?: string; onSend: () => void; onVerify: (code: string) => void; onChangeEmail: () => void; resendSeconds?: number; guestMode?: boolean; disabled?: boolean;
}) {
  const [code, setCode] = useState("");
  const [left, setLeft] = useState(resendSeconds);
  const [totpStep, setTotpStep] = useState(false);
  useEffect(() => { if (phase === "totp") { setTotpStep(true); setCode(""); } if (phase === "idle") setTotpStep(false); }, [phase]);
  useEffect(() => { if (phase === "sent" && prefill) setCode(prefill); }, [phase, prefill]);
  const guestCodeVisible = phase === "guest_sent" || phase === "guest_verifying";
  const codeVisible = phase === "sent" || phase === "verifying" || phase === "wrong" || phase === "expired" || phase === "totp" || guestCodeVisible;
  useEffect(() => {
    if (phase !== "sent" && phase !== "guest_sent") return;
    setLeft(resendSeconds);
    if (!prefill) setCode("");
    const id = setInterval(() => setLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [phase === "sent" || phase === "guest_sent", resendSeconds]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === "verified") return <p role="status">You're signed in.</p>;
  if (phase === "guest_verified") return <p role="status">Email verified. Continue as a ticket guest.</p>;
  return (
    <div>
      {!codeVisible && (
        <>
          <p>{guestMode ? "Verify this email to continue as a ticket guest. No member account will be created." : "Sign in with a 6-digit code sent to this email so your tickets are always in My Events."}</p>
          <button type="button" className="chi-btn-outline" onClick={onSend} disabled={phase === "sending" || disabled}>Send code</button>
        </>
      )}
      {phase === "send_failed" && <p role="alert">We couldn't send the code. Check your connection and try again.</p>}
      {phase === "rate_limited" && <p role="alert">Too many attempts. Wait a few minutes and try again.</p>}
      {codeVisible && (
        <div>
          <p aria-live="polite">{totpStep ? "Enter the 6-digit code from your authenticator app." : guestMode ? `Enter the code we sent to ${email} to verify this ticket guest.` : `Enter the code we sent to ${email} to sign in.`}</p>
          <button type="button" className="tp-link" onClick={onChangeEmail} style={{ background: "none", border: 0, padding: 0 }}>Change email</button>
          <InputOTP maxLength={6} value={code} onChange={v => setCode(v)} onComplete={(v: string) => onVerify(v)} autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*" aria-label={totpStep ? "Authenticator code" : "6-digit code"} aria-invalid={phase === "wrong" || undefined}>
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map(i => <InputOTPSlot key={i} index={i} style={{ width: 48, height: 48 }} />)}</InputOTPGroup>
          </InputOTP>
          {prefill && phase === "sent" && <button type="button" className="chi-btn-outline" onClick={() => onVerify(code)}>Verify code</button>}
          {(phase === "verifying" || phase === "guest_verifying") && <p role="status">Checking code...</p>}
          {phase === "wrong" && <p role="alert" className="tp-error">The code is invalid or expired. Try again or send a new code.</p>}
          {(phase === "wrong" || phase === "expired") && <button type="button" className="chi-btn-outline" onClick={onSend}>Send new code</button>}
          {(phase === "sent" || phase === "guest_sent") && (left > 0 ? <p>Resend code in 0:{String(left).padStart(2, "0")}</p> : <button type="button" className="tp-link" onClick={onSend} style={{ background: "none", border: 0 }}>Resend code</button>)}
        </div>
      )}
    </div>
  );
}

/** Attendee 1 = the buyer. `otpSlot` holds OtpInline when logged out. */
export function BuyerCard({ children, onNotYou, otpSlot }: { children: ReactNode; onNotYou?: () => void; otpSlot?: ReactNode }) {
  return (
    <section className="chi-ticket-card" aria-labelledby="tp-buyer-heading">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 id="tp-buyer-heading" style={{ display: "flex", gap: ".55rem", alignItems: "flex-start", fontSize: "1rem", color: "var(--tp-ink)", margin: "0 0 14px" }}><UserRound size={18} aria-hidden="true" /> <span>Your information <small style={{ display: "block", color: "var(--tp-muted)", fontSize: ".72rem", fontWeight: 400, marginTop: 3 }}>Primary ticket holder</small></span></h2>
        {onNotYou && <button type="button" className="tp-link" onClick={onNotYou} style={{ background: "none", border: 0 }}>Not you?</button>}
      </div>
      {children}
      {otpSlot}
    </section>
  );
}

export function TicketStepper({ value, min = 1, max, remaining, atMax, onChange }: { value: number; min?: number; max: number; remaining?: number | null; atMax?: boolean; onChange: (next: number) => void }) {
  return (
    <section className="chi-ticket-card">
      <span className="tp-label" id="tp-stepper-label">Tickets</span>
      <div className="chi-stepper" role="group" aria-label="Number of tickets" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button type="button" aria-label="Remove a ticket" onClick={() => onChange(value - 1)} disabled={value <= min}>−</button>
        <output aria-live="polite" aria-label={`${value} tickets`} style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.25rem", minWidth: "2ch", textAlign: "center", color: "white" }}>{value}</output>
        <button type="button" aria-label="Add a ticket" onClick={() => onChange(value + 1)} disabled={value >= max}>+</button>
      </div>
      <p>Max 10 per order{typeof remaining === "number" && remaining <= 20 ? ` · ${remaining} left` : ""}</p>
      {typeof remaining === "number" && remaining > 0 && remaining <= 5 && <p style={{ color: "var(--tp-warn)" }}>Only {remaining} left</p>}
      {atMax && value >= max && <p role="status">That's the most you can buy in one order.</p>}
    </section>
  );
}

export function GuestCard({ index, onRemove, showHelper, children }: { index: number; onRemove: () => void; showHelper?: boolean; children: ReactNode }) {
  return (
    <section className="chi-ticket-card tp-guest-row" aria-labelledby={`tp-guest-${index}`}>
      <div className="tp-guest-row-header">
        <h3 id={`tp-guest-${index}`} className="tp-sr">Guest {index}</h3>
        <span className="tp-guest-ticket-label">Ticket {index + 1}</span>
        <button type="button" aria-label={`Remove guest ${index}`} onClick={onRemove} style={{ minWidth: 44, minHeight: 44, background: "none", border: 0, color: "inherit" }}>Remove</button>
      </div>
      {showHelper && <span className="tp-sr">Each guest receives their own QR ticket by email.</span>}
      {children}
    </section>
  );
}

export function StruckPrice({ cents, currency }: { cents: number; currency?: string }) {
  return (
    <s style={{ color: "#94A3B8" }}>
      <span className="tp-sr">Regular price </span>{formatCents(cents, currency)}
    </s>
  );
}

export function PromotionCodeEntry({ value, quote, error, loading, needsAction, disabled, onChange, onApply, onRemove }: {
  value: string; quote: TicketPromotionQuote | null; error: string | null; loading: boolean; needsAction: boolean; disabled?: boolean;
  onChange: (value: string) => void; onApply: () => void; onRemove: () => void;
}) {
  const applied = Boolean(quote && !needsAction);
  const stripeEntryRequired = Boolean(error?.startsWith("This first-purchase code must be entered"));
  return (
    <section aria-label="Promotion code" style={{ borderTop: "1px solid rgba(255,255,255,.1)", paddingTop: "1rem", marginTop: "1rem" }}>
      <label className="tp-label" htmlFor="ticket-promotion-code">Promotion code</label>
      <div style={{ display: "flex", gap: ".5rem", alignItems: "stretch" }}>
        <input
          id="ticket-promotion-code"
          value={value}
          maxLength={64}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          onChange={event => onChange(event.target.value)}
          readOnly={disabled}
          aria-invalid={Boolean(error || needsAction) || undefined}
          aria-describedby={error ? "ticket-promotion-feedback" : undefined}
          style={{ ...inputStyle, minWidth: 0, flex: 1, textTransform: "uppercase" }}
        />
        {applied || stripeEntryRequired ? (
          <button type="button" className="chi-btn-outline" onClick={onRemove} disabled={disabled}>Remove</button>
        ) : (
          <button type="button" className="chi-btn-outline" onClick={onApply} disabled={disabled || loading || !value.trim()}>Apply</button>
        )}
      </div>
      {loading && <p role="status" aria-live="polite" style={{ marginBottom: 0 }}>Checking code…</p>}
      {error && <p id="ticket-promotion-feedback" role="alert" className="tp-error">{error}</p>}
      {applied && <p role="status" style={{ color: "#F9A8D4", marginBottom: 0 }}>Code {quote?.code} applied.</p>}
      {needsAction && !loading && !error && <p role="status" style={{ marginBottom: 0 }}>Apply this code again to check the updated ticket price.</p>}
    </section>
  );
}

export function OrderSummary({ qty, unitCents, fullCents, promoLabel, currency = "usd", children, promotionQuote, automaticSavingsCents, ticketHeader }: {
  qty: number; unitCents: number; fullCents?: number | null; promoLabel?: string | null; currency?: string; children?: ReactNode; ticketHeader?: ReactNode;
  promotionQuote?: TicketPromotionQuote | null; automaticSavingsCents?: number;
}) {
  const showFull = typeof fullCents === "number" && fullCents > unitCents;
  const total = promotionQuote?.totalCents ?? unitCents * qty;
  const regularSubtotal = showFull ? (fullCents as number) * qty : unitCents * qty;
  const afterAutomaticSubtotal = promotionQuote?.subtotalCents ?? unitCents * qty;
  return (
    <section className="chi-ticket-card tp-checkout-summary" aria-label="Order summary" data-testid="order-summary">
      {ticketHeader}
      <h2 className="tp-order-heading"><ReceiptText size={17} aria-hidden="true" /> Order summary</h2>
      <div className="tp-order-summary-lines">
      <p><span>{showFull ? "Regular subtotal" : "Subtotal"} · {qty} {qty === 1 ? "ticket" : "tickets"}</span><span>{formatCents(regularSubtotal, currency)}</span></p>
      {showFull && <p style={{ color: "#ddaacb" }}><span>{promoLabel ?? "Automatic promo"} savings </span><span>−{formatCents(automaticSavingsCents ?? ((fullCents as number) - unitCents) * qty, currency)}</span></p>}
      {showFull && <p><span>Subtotal after automatic savings</span><span>{formatCents(afterAutomaticSubtotal, currency)}</span></p>}
      {promotionQuote && <>
        <p style={{ color: "#ddaacb" }}><span>Code savings </span><span>−{formatCents(promotionQuote.discountCents, promotionQuote.currency)}</span></p>
      </>}
      </div>
      <p className="tp-total-row"><span>Total due</span><strong data-testid="ticket-total">{formatCents(total, currency)}</strong></p>
      <p style={{ fontSize: "0.8rem" }}>Taxes and fees, if any, are shown at checkout.</p>
      {children}
    </section>
  );
}

export function StickyTotalBar({ qty, totalCents, fullTotalCents, currency, cta, holdSlot, codeSavingsCents, automaticSavingsCents }: { qty: number; totalCents: number; fullTotalCents?: number | null; currency?: string; cta: ReactNode; holdSlot?: ReactNode; codeSavingsCents?: number; automaticSavingsCents?: number }) {
  return (
    <div className="chi-sticky-total">
      <div>
        <span>{qty} {qty === 1 ? "ticket" : "tickets"} · {formatCents(totalCents, currency)}</span>{" "}
        {typeof fullTotalCents === "number" && fullTotalCents > totalCents && <StruckPrice cents={fullTotalCents} currency={currency} />}
        {typeof automaticSavingsCents === "number" && automaticSavingsCents > 0 && <div style={{ color: "#F9A8D4", fontSize: ".8rem" }}>Automatic promo savings −{formatCents(automaticSavingsCents, currency)}</div>}
        {typeof codeSavingsCents === "number" && codeSavingsCents > 0 && <div style={{ color: "#F9A8D4", fontSize: ".8rem" }}>Includes code savings −{formatCents(codeSavingsCents, currency)}</div>}
        {holdSlot && <div style={{ fontSize: "0.8rem" }}>{holdSlot}</div>}
      </div>
      {cta}
    </div>
  );
}

export function ContinueCta({ state, reason, onClick }: { state: "idle" | "disabled" | "loading"; reason?: string; onClick: () => void }) {
  return (
    <div>
      <button type="submit" className="chi-btn-primary" onClick={onClick} disabled={state !== "idle"} aria-busy={state === "loading" || undefined} style={{ width: "100%" }}>
        {state === "loading" ? "Opening secure checkout..." : reason === "Sold out" ? "Sold out" : "Continue to payment"}
      </button>
      {state === "disabled" && reason && reason !== "Sold out" && <p>{reason}</p>}
      <p style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: ".35rem", fontSize: "0.8rem" }}><LockKeyhole size={13} aria-hidden="true" /> Secure checkout by Stripe</p>
    </div>
  );
}

function mmss(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Spec 3.10: server-driven hold countdown. Never a client-started timer. */
export type HoldTimerProps = {
  orderId: number;
  expiresAt: string;
  serverNow: string;
  receivedAt: number;
  status?: string;
  action?: "renew" | "expire" | "cancel" | null;
  message?: string | null;
  paid?: boolean;
  restartRequired?: boolean;
  onExpire: () => void;
  onRenew?: () => void;
  onCancel?: () => void;
};

export function HoldTimer({ orderId, expiresAt, serverNow, receivedAt, status = "pending", action = null, message, paid = false, restartRequired = false, onExpire, onRenew, onCancel }: HoldTimerProps) {
  const [clientNow, setClientNow] = useState(Date.now);
  const deadlineClient = useMemo(() => holdDeadlineOnClient(orderId, expiresAt, serverNow, receivedAt), [orderId, expiresAt, serverNow, receivedAt]);
  const serverDuration = Date.parse(expiresAt) - Date.parse(serverNow);
  const remaining = Math.min(deadlineClient - clientNow, Number.isFinite(serverDuration) ? serverDuration : Infinity);
  const fired = useRef(false);
  const [announce, setAnnounce] = useState("");
  useEffect(() => {
    const interval = setInterval(() => setClientNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (remaining <= 0 && !fired.current) {
      fired.current = true;
      onExpire();
    }
    if (remaining > 0) fired.current = false;
    const s = Math.ceil(remaining / 1000);
    if (s === 300) setAnnounce("5 minutes left on your hold");
    if (s === 60) setAnnounce("1 minute left on your hold");
  }, [remaining, onExpire]);
  const warn = remaining < 5 * 60_000;
  const needsAttention = remaining <= 60_000;
  const isBusy = action !== null;
  const closing = status === "closing";
  const processing = status === "processing";
  const retryLabel = processing ? "Check payment status" : closing ? "Retry closure check" : "Retry status check";
  return (
    <div data-testid="hold-timer-wrap">
      <div role="timer" aria-live="off" data-testid="hold-timer" style={{ color: warn ? "var(--tp-warn)" : "#CBD5E1" }}>
        {paid ? "Payment confirmed in another tab" : processing ? "Checking payment status" : closing ? `Confirming checkout closure · ${mmss(remaining)}` : warn ? `Hold ends in ${mmss(remaining)}` : `Price and seats held for ${mmss(remaining)}`}
      </div>
      <span className="tp-sr" aria-live="polite">{announce}</span>
      {message && !needsAttention && <div className="tp-hold-inline-status" role="status">{message}{(closing || remaining <= 0) && <button type="button" onClick={onExpire} disabled={isBusy}>{retryLabel}</button>}</div>}
      <Dialog open={needsAttention && !paid} onOpenChange={() => undefined}>
        <DialogContent
          className="tp-hold-dialog"
          showCloseButton={false}
          onEscapeKeyDown={event => event.preventDefault()}
          onPointerDownOutside={event => event.preventDefault()}
        >
          <p className="tp-hold-dialog__eyebrow">Your seats are on hold</p>
          <DialogTitle className="tp-hold-dialog__title">{processing ? "Checking payment status" : "Are you still there?"}</DialogTitle>
          <DialogDescription className="tp-hold-dialog__description">
            {processing
              ? "This checkout is still being verified. Keep this page open while we check for an update."
              : restartRequired
              ? "This checkout can't be extended. Cancel this order and restart checkout for a fresh 8-minute hold."
              : remaining > 0
              ? `Your checkout hold ends in ${mmss(remaining)}. Stay on this page while Stripe checkout is open; you can extend the hold for another 8 minutes.`
              : "The hold deadline has passed. We're checking with the server before confirming whether checkout has closed."}
          </DialogDescription>
          {message && <p className="tp-hold-dialog__status" role="status">{message}</p>}
          <div className="tp-hold-dialog__actions">
            {remaining > 0 && status !== "closing" && !processing && !restartRequired && onRenew && (
              <button type="button" className="tp-hold-dialog__renew" onClick={onRenew} disabled={isBusy}>
                {action === "renew" ? "Extending hold…" : "I'm still here"}
              </button>
            )}
            {(remaining <= 0 || closing || Boolean(message)) && (
              <button type="button" className="tp-hold-dialog__retry" onClick={onExpire} disabled={isBusy}>
                {action === "expire" ? "Checking with server…" : retryLabel}
              </button>
            )}
            {onCancel && (
              <button type="button" className="tp-hold-dialog__cancel" onClick={onCancel} disabled={isBusy}>
                {action === "cancel" ? "Canceling checkout…" : "Cancel order"}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function HoldExpiredNotice({ onRetry, message }: { onRetry: () => void; message?: string }) {
  return (
    <div role="status" data-testid="hold-ended-notice">
      <p>{message ?? "Your hold has ended. These seats and this price are no longer held for you."}</p>
      <button type="button" className="chi-btn-primary" onClick={onRetry}>Check out again</button>
    </div>
  );
}
